import { openSignedUrl } from '@/lib/openSignedUrl';
import { ContractInsightsData } from '@/pages/dashboard/ContractInsights';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { format } from 'date-fns';
import { businessDay, formatBusinessDate } from '@/lib/format';
import { Wallet, TrendingUp, ChevronDown, Plus, Pencil, CalendarDays, FileSignature } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import RecordLoanRepaymentSheet from '@/components/contracts/RecordLoanRepaymentSheet';
import { LoanAllocationSheet } from '@/components/contracts/insights/LoanAllocationSheet';
import { carriesWeekly } from '@/components/contracts/insights/carriesWeekly';
import type { ContractAgreementsModel } from '@/components/agreements/AgreementSlotList';
import { advanceAgreementStatus, loanSlotsForLoan } from '@/components/agreements/boardSlots';

/**
 * 'dd MMM yyyy' in the business timezone — the compact shape the expanded loan's card grid uses,
 * kept because the cards beside it print the same one. `businessDay` first, so the rendered day is
 * the Ghana day the money moved rather than the viewer's reading of that instant.
 */
const formatBusinessDateCompact = (value: string | null | undefined): string => {
    const day = businessDay(value);
    return day ? format(day, 'dd MMM yyyy') : '-';
};

interface ContractInsightLoansProps {
    data: ContractInsightsData;
    contractId: number;
    /**
     * THE PAGE'S ONE SIGNING MODEL, READ AT `junctionScope: 'all'` — `ContractInsightInsurance`'s
     * prop, for its reasons.
     *
     * REQUIRED, NOT OPTIONAL. A caller that could leave it out would grade the advances on some
     * pages and silently not on others, and it must be the PAGE's instance: `useContractAgreements`
     * registers seven query keys, and a second declaration of them under this tab is the shape that
     * made the insurance read return two different rows for one cache entry.
     *
     * TWO THINGS ARE READ OFF IT AND NOTHING IS WRITTEN THROUGH IT. The purpose each advance was
     * opened for (`loans.description`, which `get_contract_insights` does not carry — its
     * `loans[].description` is the bank transaction's narration) and the state of its Schedule.
     */
    agreements: ContractAgreementsModel;
    onRefresh?: () => void;
    onAddLoan: () => void;
    onEditLoan: (loanId: number) => void;
    /**
     * WHERE A LOAN AREA KEY GOES, ON ONE NAMED ADVANCE — this card's own resolver, never the
     * contract's bare setup route.
     *
     * Every key the Schedule's vocabulary can produce has to land somewhere: `loan.` keys are edited
     * on the advance dossier and everything else on the contract's setup canvas. The loan id travels
     * with the area because an advance area is a place on ONE advance's dossier, and the card is
     * what knows which — a resolver without it would have to guess, and the guess would be wrong on
     * the third advance of a contract.
     */
    onDeepLink: (loanId: number, area: string) => void;
}

export default function ContractInsightLoans({
    data,
    contractId,
    agreements,
    onRefresh,
    onAddLoan,
    onEditLoan,
    onDeepLink,
}: ContractInsightLoansProps) {
    const [expandedLoan, setExpandedLoan] = useState<number | null>(null);
    const [repaymentSheetOpen, setRepaymentSheetOpen] = useState(false);
    const [selectedLoanId, setSelectedLoanId] = useState<number | null>(null);
    const [selectedLoanDescription, setSelectedLoanDescription] = useState<string | undefined>(undefined);
    const [selectedRepaymentId, setSelectedRepaymentId] = useState<number | null>(null);
    const [allocationSheetOpen, setAllocationSheetOpen] = useState(false);
    const [allocationSheetLoanId, setAllocationSheetLoanId] = useState<number | null>(null);
    const [allocationSheetTitle, setAllocationSheetTitle] = useState('');

    if (!data.loans || data.loans.length === 0) {
        return (
            <div className="border border-dashed rounded-xl p-12 text-center bg-muted/5">
                <div className="h-12 w-12 mx-auto rounded-full bg-muted/20 flex items-center justify-center text-muted-foreground">
                    <Wallet className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-medium mt-4">No Advances</h3>
                <p className="text-sm text-muted-foreground mt-1 mb-6 max-w-xs mx-auto">
                    This contract currently has no active advances.
                </p>
                <Button onClick={onAddLoan}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Advance
                </Button>
            </div>
        );
    }

    const totalLoanAmount = data.loans.reduce((sum, l) => sum + l.amount, 0);
    const totalRepaid = data.loans.reduce((sum, l) => sum + (l.allocated_total || 0) + (l.repayments_total || 0), 0);
    const totalInterest = data.loans.reduce((sum, l) => sum + (l.amount * (l.interest_rate || 0) / 100), 0);
    const totalOwed = totalLoanAmount + totalInterest;

    return (
        <div className="space-y-8">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <StatCard
                    title="Total Repayable"
                    value={<CurrencyDisplay amount={totalOwed} />}
                    description="The total amount owed across all advances, including both principal and interest charges."
                />
                <StatCard
                    title="Advance"
                    value={<CurrencyDisplay amount={totalLoanAmount} />}
                    description="The combined principal amount of all advances issued under this contract."
                />
                <StatCard
                    title="Interest"
                    value={<CurrencyDisplay amount={totalInterest} />}
                    description="The total interest charges calculated on all advance principals based on their respective interest rates."
                />
                <StatCard
                    title="Repaid"
                    value={<CurrencyDisplay amount={totalRepaid} />}
                    description="The total amount already repaid across all advances, including both direct payments and allocations from contract repayments."
                />
                <StatCard
                    title="Instalment"
                    value={<CurrencyDisplay amount={data.loans.reduce((sum, loan) => {
                        const loanTotal = loan.amount + (loan.amount * (loan.interest_rate || 0) / 100);
                        const repaid = (loan.allocated_total || 0) + (loan.repayments_total || 0);
                        // Use 99.9% threshold to handle floating point precision
                        const isPaidOff = repaid >= loanTotal * 0.999;
                        if (isPaidOff) return sum;
                        const latestInstalment = loan.instalments?.[0]?.amount || 0;
                        return sum + latestInstalment;
                    }, 0)} />}
                    description="The combined weekly instalment amount for all active (unpaid) advances."
                />
            </div>

            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold tracking-tight">Advances</h3>
                    <Button variant="outline" size="sm" onClick={onAddLoan}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add
                    </Button>
                </div>

                <div className="space-y-4">
                    {data.loans.map((loan) => {
                        const isExpanded = expandedLoan === loan.id;
                        const loanTotal = loan.amount + (loan.amount * (loan.interest_rate || 0) / 100);
                        const totalRepaid = (loan.allocated_total || 0) + (loan.repayments_total || 0);
                        const repaidPercent = loanTotal > 0 ? Math.min(100, (totalRepaid / loanTotal) * 100) : 0;
                        // Use 99.9% threshold to handle floating point precision issues
                        const isPaidOff = totalRepaid >= loanTotal * 0.999;
                        // Does this advance carry a weekly of its own, or is it settled at the end of
                        // the contract — see `carriesWeekly`, which the Insurance tab reads too.
                        const hasInstalment = carriesWeekly(loan);

                        /**
                         * WHAT THIS ADVANCE IS FOR — `loans.description`, through the board's subjects.
                         *
                         * NEVER `loan.description` OFF THIS PAYLOAD, AND THE TWO ARE GENUINELY
                         * DIFFERENT STRINGS. `get_contract_insights` publishes the DISBURSEMENT
                         * TRANSACTION's narration under that key — a bank line, written for a
                         * statement — while the Schedule prints `loans.description` as
                         * `{{loan.purpose}}`; they differ on 160 of the 161 production advances. The
                         * line under the heading has to be the sentence the driver signs, so it is
                         * read from the subjects the signing board was built from.
                         *
                         * ABSENT PRINTS NOTHING. Blank is a real state — the column is nullable and
                         * the Schedule refuses to render without it — and the transaction's
                         * narration is exactly the wrong thing to fall back to: it would print a
                         * purpose the instrument does not carry.
                         */
                        const purpose =
                            agreements.subjects?.loans.find((subject) => subject.id === loan.id)
                                ?.description?.trim() || null;

                        /** This advance's row on the signing board — see `advanceAgreementStatus`. */
                        const agreementSlot = loanSlotsForLoan(agreements.slots, loan.id)[0] ?? null;

                        return (
                            <motion.div
                                layout
                                key={loan.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.2 }}
                            >
                                <Card
                                    className={cn(
                                        "overflow-hidden border shadow-sm transition-all duration-200",
                                        isExpanded ? "ring-1 ring-primary/20 shadow-md" : "hover:border-primary/20 hover:shadow-md"
                                    )}
                                >
                                    <div
                                        className="relative cursor-pointer group"
                                        onClick={() => setExpandedLoan(isExpanded ? null : loan.id)}
                                    >
                                        <div className="p-5 flex items-start gap-4">
                                            {/* Icon Box */}
                                            <div className={cn(
                                                "p-2.5 rounded-xl transition-colors shrink-0",
                                                isPaidOff
                                                    ? "bg-emerald-100/50 text-emerald-600 dark:bg-emerald-900/20"
                                                    : "bg-primary/10 text-primary dark:bg-primary/20"
                                            )}>
                                                <Wallet className="h-5 w-5" />
                                            </div>

                                            {/* Main Content */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-4">
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <h4 className="font-semibold text-base truncate">
                                                                Loan #{loan.id}
                                                            </h4>
                                                            {(hasInstalment || isPaidOff) && (
                                                                <Badge
                                                                    variant="secondary"
                                                                    className={cn(
                                                                        "font-normal text-xs px-2 py-0 h-5",
                                                                        isPaidOff
                                                                            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                                                                            : "bg-blue-50 text-blue-700 hover:bg-blue-50"
                                                                    )}
                                                                >
                                                                    {isPaidOff ? 'Paid Off' : 'Active'}
                                                                </Badge>
                                                            )}
                                                            {/* AN ADVANCE WITH NO WEEKLY IS AN ARRANGEMENT, NOT AN OMISSION
                                                                [D-B58]. Nothing separate is collected while the vehicle is
                                                                being paid for; once it is paid for the driver keeps paying
                                                                the contract instalment until the advance is recovered. So
                                                                the badge is neutral — there is no missing schedule for an
                                                                admin to go and set, and amber said there was. The insurance
                                                                side of the same arrangement reads the same words in the same
                                                                treatment [D-B57].

                                                                AND IT LEAVES ONCE THE ADVANCE IS RECOVERED, because by then
                                                                the arrangement is behind it: "Paid Off" beside it has the
                                                                last word, and a sentence about how this will be settled is
                                                                not true of an advance that already is. Every sibling drops
                                                                at that same moment — the insurance badge, and the striped
                                                                fill on the bar below. */}
                                                            {!hasInstalment && !isPaidOff && (
                                                                <Badge
                                                                    variant="outline"
                                                                    className="font-normal text-xs px-2 py-0 h-5 text-muted-foreground border-muted-foreground/30"
                                                                >
                                                                    Settled at end of contract
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        {purpose && (
                                                            <p className="mb-1 text-sm text-muted-foreground">
                                                                {purpose}
                                                            </p>
                                                        )}
                                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                                                            <div className="flex items-center gap-1.5">
                                                                <CalendarDays className="h-3.5 w-3.5" />
                                                                {/* `lent_at` is a money instant: the Ghana day the cash left. */}
                                                                <span>{formatBusinessDate(loan.lent_at)}</span>
                                                            </div>
                                                            {loan.interest_rate && (
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className="w-1 h-1 rounded-full bg-muted-foreground/30" />
                                                                    <TrendingUp className="h-3.5 w-3.5" />
                                                                    <span>{loan.interest_rate}% interest</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="flex items-center gap-1">
                                                        <Button
                                                            size="icon"
                                                            variant="ghost"
                                                            className="h-8 w-8 text-muted-foreground hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity"
                                                            title="Edit Advance"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onEditLoan(loan.id);
                                                            }}
                                                        >
                                                            <Pencil className="h-3.5 w-3.5" />
                                                            <span className="sr-only">Edit</span>
                                                        </Button>
                                                        {/*
                                                          * TWO DOORS ONTO ONE ROW, AND WHICH ONE IS
                                                          * DRAWN IS THE RECORD'S OWN SHAPE.
                                                          *
                                                          * AN IN-APP SCHEDULE IS A CEREMONY, NOT A
                                                          * FILE. Once a LOAN document exists the
                                                          * useful gesture is not "open the PDF" — it
                                                          * is send it, countersign it, void it,
                                                          * watch it seal — and every one of those
                                                          * lives on the advance dossier's Agreement
                                                          * area, which is where this goes.
                                                          *
                                                          * A PAPER ORIGINAL KEEPS THE SCAN [F3]. All
                                                          * 161 production advances are paper: a
                                                          * signed `loan_agreements` row and no
                                                          * document, which `agreement_create`
                                                          * refuses to add one to. There is no
                                                          * ceremony to open, so the scan IS the
                                                          * record and this stays the only door to
                                                          * it.
                                                          */}
                                                        {agreementSlot?.document ? (
                                                            <Button
                                                                size="icon"
                                                                variant="ghost"
                                                                className="h-8 w-8 text-muted-foreground hover:text-primary"
                                                                title="Open the advance agreement"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onDeepLink(loan.id, 'agreement');
                                                                }}
                                                            >
                                                                <FileSignature className="h-3.5 w-3.5" />
                                                                <span className="sr-only">Open agreement</span>
                                                            </Button>
                                                        ) : (
                                                            loan.agreement?.agreement_url && (
                                                                <Button
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    className="h-8 w-8 text-muted-foreground hover:text-primary"
                                                                    title="View Advance Agreement"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        openSignedUrl(loan.agreement!.agreement_url);
                                                                    }}
                                                                >
                                                                    <FileSignature className="h-3.5 w-3.5" />
                                                                    <span className="sr-only">View Agreement</span>
                                                                </Button>
                                                            )
                                                        )}
                                                        <div className={cn(
                                                            "ml-1 w-8 h-8 flex items-center justify-center rounded-full transition-transform duration-200",
                                                            isExpanded ? "bg-muted rotate-180" : "hover:bg-muted"
                                                        )}>
                                                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-6">
                                                    <div>
                                                        <p className="text-xs font-medium text-muted-foreground mb-1">Principal</p>
                                                        <p className="text-sm font-semibold"><CurrencyDisplay amount={loan.amount} /></p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-medium text-muted-foreground mb-1">Total Repayable</p>
                                                        <p className="text-sm font-semibold"><CurrencyDisplay amount={loanTotal} /></p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-medium text-muted-foreground mb-1">Repaid</p>
                                                        <div className="flex items-baseline gap-1.5">
                                                            <p className="text-sm font-semibold"><CurrencyDisplay amount={totalRepaid} /></p>
                                                            <span className="text-xs text-muted-foreground">({repaidPercent.toFixed(0)}%)</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Progress Bar */}
                                                <div className="mt-5">
                                                    <div className="h-2 bg-muted rounded-full overflow-hidden relative">
                                                        <div
                                                            className={cn("h-full transition-all duration-500 relative z-10",
                                                                isPaidOff ? "bg-emerald-500" : "bg-primary"
                                                            )}
                                                            style={{ width: `${repaidPercent}%` }}
                                                        />
                                                        {!hasInstalment && !isPaidOff && (
                                                            <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(0,0,0,0.05)_25%,rgba(0,0,0,0.05)_50%,transparent_50%,transparent_75%,rgba(0,0,0,0.05)_75%,rgba(0,0,0,0.05)_100%)] bg-[length:10px_10px]" />
                                                        )}
                                                    </div>
                                                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                                                        <span>Repaid: <CurrencyDisplay amount={totalRepaid} /></span>
                                                        <span>Total: <CurrencyDisplay amount={loanTotal} /></span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded Details */}
                                    <AnimatePresence>
                                        {isExpanded && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <CardContent className="px-5 pb-5 pt-0 border-t bg-muted/5">
                                                    <div className="pt-5">
                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                                            <div className="bg-background rounded-lg p-3 border shadow-sm">
                                                                <p className="text-xs text-muted-foreground mb-1">Advance Principal</p>
                                                                <p className="font-semibold text-sm"><CurrencyDisplay amount={loan.amount} /></p>
                                                            </div>
                                                            <div className="bg-background rounded-lg p-3 border shadow-sm">
                                                                <p className="text-xs text-muted-foreground mb-1">Interest Rate</p>
                                                                <p className="font-semibold text-sm flex items-center gap-1">
                                                                    {loan.interest_rate || 0}%
                                                                    <TrendingUp className="h-3 w-3 text-muted-foreground" />
                                                                </p>
                                                            </div>
                                                            <div className="bg-background rounded-lg p-3 border shadow-sm">
                                                                <p className="text-xs text-muted-foreground mb-1">Date Issued</p>
                                                                <p className="font-semibold text-sm">
                                                                    {formatBusinessDateCompact(loan.lent_at)}
                                                                </p>
                                                            </div>
                                                            <div className="bg-background rounded-lg p-3 border shadow-sm">
                                                                <p className="text-xs text-muted-foreground mb-1">Agreement</p>
                                                                <Badge
                                                                    variant="outline"
                                                                    className="font-normal text-xs"
                                                                >
                                                                    {advanceAgreementStatus(agreementSlot)}
                                                                </Badge>
                                                            </div>
                                                        </div>

                                                        {/* Direct Payments */}
                                                        <div className="mb-6">
                                                            <div className="flex items-center justify-between mb-3">
                                                                <h5 className="text-sm font-semibold flex items-center gap-2">
                                                                    <Wallet className="h-4 w-4 text-muted-foreground" />
                                                                    Direct Payments
                                                                </h5>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-7 text-xs"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSelectedLoanId(loan.id);
                                                                        setSelectedRepaymentId(null);
                                                                        setSelectedLoanDescription(loan.description || `Advance #${loan.id}`);
                                                                        setRepaymentSheetOpen(true);
                                                                    }}
                                                                >
                                                                    <Plus className="h-3.5 w-3.5 mr-1" />
                                                                    Add
                                                                </Button>
                                                            </div>
                                                            {loan.repayments && loan.repayments.length > 0 ? (
                                                                <div className="bg-background rounded-xl border divide-y overflow-hidden max-h-[150px] overflow-y-auto">
                                                                    {loan.repayments.map((repayment: { id: number; transaction_id: number; description: string; amount: number; paid_at: string }) => (
                                                                        <div key={repayment.id} className="flex justify-between items-center px-4 py-2.5 hover:bg-muted/30 transition-colors group/row">
                                                                            <div className="flex flex-col">
                                                                                <span className="text-sm font-medium">
                                                                                    {formatBusinessDate(repayment.paid_at)}
                                                                                </span>
                                                                                <span className="text-xs text-muted-foreground">
                                                                                    {repayment.description || 'Repayment'}
                                                                                </span>
                                                                            </div>
                                                                            <div className="flex items-center gap-2">
                                                                                <Badge variant="outline" className="text-emerald-600 bg-emerald-50 border-emerald-100 font-mono">
                                                                                    +<CurrencyDisplay amount={repayment.amount} />
                                                                                </Badge>
                                                                                <Button
                                                                                    size="icon"
                                                                                    variant="ghost"
                                                                                    className="h-6 w-6 text-muted-foreground hover:text-primary opacity-0 group-hover/row:opacity-100 transition-opacity"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setSelectedLoanId(loan.id);
                                                                                        setSelectedRepaymentId(repayment.id);
                                                                                        setSelectedLoanDescription(loan.description || `Advance #${loan.id}`);
                                                                                        setRepaymentSheetOpen(true);
                                                                                    }}
                                                                                >
                                                                                    <Pencil className="h-3 w-3" />
                                                                                </Button>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <div className="text-center py-4 border-2 border-dashed rounded-xl">
                                                                    <p className="text-xs text-muted-foreground">No direct payments yet.</p>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Allocation History */}
                                                        <div>
                                                            <div className="flex items-center justify-between mb-3">
                                                                <h5 className="text-sm font-semibold flex items-center gap-2">
                                                                    <Wallet className="h-4 w-4 text-muted-foreground" />
                                                                    Allocation History
                                                                    <span className="text-xs text-muted-foreground font-normal">
                                                                        (from contract repayments)
                                                                    </span>
                                                                </h5>
                                                            </div>
                                                            {loan.recent_allocations && loan.recent_allocations.length > 0 ? (
                                                                <div className="space-y-2">
                                                                    <div className="bg-background rounded-xl border divide-y overflow-hidden">
                                                                        {loan.recent_allocations.slice(0, 5).map((alloc) => (
                                                                            <div key={alloc.repayment_id} className="flex justify-between items-center px-4 py-2.5 hover:bg-muted/30 transition-colors">
                                                                                <div className="flex flex-col">
                                                                                    <span className="text-sm font-medium">
                                                                                        {formatBusinessDate(alloc.payment_date)}
                                                                                    </span>
                                                                                    <span className="text-xs text-muted-foreground">
                                                                                        from <CurrencyDisplay amount={alloc.payment_amount} /> payment
                                                                                    </span>
                                                                                </div>
                                                                                <Badge variant="outline" className="text-blue-600 bg-blue-50 border-blue-100 font-mono">
                                                                                    →<CurrencyDisplay amount={alloc.amount} />
                                                                                </Badge>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                    {loan.allocation_count > 5 && (
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="w-full text-xs"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setAllocationSheetLoanId(loan.id);
                                                                                setAllocationSheetTitle(loan.description || `Advance #${loan.id}`);
                                                                                setAllocationSheetOpen(true);
                                                                            }}
                                                                        >
                                                                            View all {loan.allocation_count} allocations
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div className="text-center py-8 border-2 border-dashed rounded-xl">
                                                                    <p className="text-sm text-muted-foreground">No allocations from contract repayments yet.</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </Card>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* Record/Edit Repayment Sheet */}
            {selectedLoanId && (
                <RecordLoanRepaymentSheet
                    open={repaymentSheetOpen}
                    onOpenChange={(open) => {
                        setRepaymentSheetOpen(open);
                        if (!open) setSelectedRepaymentId(null);
                    }}
                    loanId={selectedLoanId}
                    loanDescription={selectedLoanDescription}
                    repaymentId={selectedRepaymentId}
                    onSuccess={onRefresh}
                />
            )}

            {/* Allocation History Sheet */}
            {allocationSheetLoanId && (
                <LoanAllocationSheet
                    open={allocationSheetOpen}
                    onOpenChange={setAllocationSheetOpen}
                    contractId={contractId}
                    loanId={allocationSheetLoanId}
                    loanType="LOAN"
                    title={allocationSheetTitle}
                />
            )}
        </div>
    );
}
